import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Divider,
  LinearProgress,
  Typography,
} from "@mui/material";
import React from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass";

import { WithAPICall } from "../utils/apiUtil";
import emotionColors from "./emotionColors";
import styles from "./Eq.module.css";
import initAai from "./initAai";
import initHumeSocket from "./initHumeSocket";

const colors = ["red", "orange", "yellow", "green", "purple"];

const uniforms = {
  u_time: { type: "f", value: 0.0 },
  u_frequency: { type: "f", value: 0.0 },
  u_red: { type: "f", value: 1 },
  u_green: { type: "f", value: 1 },
  u_blue: { type: "f", value: 1 },
};

function getDims() {
  if (window.innerWidth > 600) {
    return {
      height: "600px",
      width: `${window.innerWidth - 300 - 320}px`,
    };
  } else {
    return {
      height: "300px",
      width: `${window.innerWidth * 0.9}px`,
    };
  }
}

function hexToRGB(hex) {
  // Remove the '#' if present
  hex = hex.replace("#", "");

  // Check if the hex code is in the short or long format
  if (hex.length === 3) {
    // Convert short format to long format
    hex = hex.replace(/(.)/g, "$1$1");
  }

  // Convert hex to decimal values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Return the RGB values as an object
  return { r, g, b };
}

// Example usage:
const hexCode = "#00ff00"; // Green color
const rgb = hexToRGB(hexCode);
console.log(rgb); // Output: { r: 0, g: 1, b: 0 }

class Eq extends React.Component {
  state = {
    humeDetails: [],
    aaiLoaded: false,
    transcript: "",
  };
  threeDivRef = React.createRef();

  handleHumeMessage = (data) => {
    this.setState({ humeDetails: data });
  };
  handleAaiMessage = (data) => {
    this.setState({ transcript: data });
  };
  handleGlobeColorChange = ({ r, g, b }) => {
    uniforms.u_red.value = Number(r);
    uniforms.u_blue.value = Number(g);
    uniforms.u_green.value = Number(b);
  };

  async componentDidMount() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.audioStream = stream;
    /**
     * socket things
     */
    const socketIOSocket = initHumeSocket({
      onHumeMessage: this.handleHumeMessage,
    });
    const aaiSocket = initAai({
      socketIOSocket,
      onMessage: this.handleAaiMessage,
      apiCallPost: this.props.apiCallPost,
      onOpen: () => {
        console.log("aai loaded");
        this.setState({ aaiLoaded: true });
        this.handleGlobeColorChange({ r: 0.4, g: 0.3, b: 0.7 });
      },
      stream,
    });

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    document.body.appendChild(renderer.domElement);
    // renderer.setClearColor(0x222222, 1);
    renderer.setClearColor(0xffffff, 0);

    const currentDiv = this.threeDivRef.current;
    renderer.setSize(currentDiv.offsetWidth, currentDiv.offsetHeight);
    currentDiv.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    const params = {
      red: 1.0,
      green: 1.0,
      blue: 1.0,
      threshold: 0.5,
      strength: 0.4,
      radius: 0.8,
    };

    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const renderScene = new RenderPass(scene, camera);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight)
    );
    bloomPass.threshold = params.threshold;
    bloomPass.strength = params.strength;
    bloomPass.radius = params.radius;

    const bloomComposer = new EffectComposer(renderer);
    bloomComposer.addPass(renderScene);
    bloomComposer.addPass(bloomPass);

    const outputPass = new OutputPass();
    bloomComposer.addPass(outputPass);

    camera.position.set(0, -2, 14);
    camera.lookAt(0, 0, 0);

    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: document.getElementById("vertexshader").textContent,
      fragmentShader: document.getElementById("fragmentshader").textContent,
    });

    const geo = new THREE.IcosahedronGeometry(4, 30);
    const mesh = new THREE.Mesh(geo, mat);
    scene.add(mesh);
    mesh.material.wireframe = true;

    let mouseX = 0;
    let mouseY = 0;
    document.addEventListener("mousemove", function (e) {
      let windowHalfX = window.innerWidth / 2;
      let windowHalfY = window.innerHeight / 2;
      mouseX = (e.clientX - windowHalfX) / 100;
      mouseY = (e.clientY - windowHalfY) / 100;
    });

    const clock = new THREE.Clock();

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    function calculateAverageFrequency() {
      analyser.getByteFrequencyData(dataArray);

      let sum = 0;
      let count = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += (i / bufferLength) * dataArray[i];
        count += dataArray[i];
      }

      const averageFrequency = count === 0 ? 0 : sum / count;
      animate(averageFrequency.toFixed(4) * 1000);

      requestAnimationFrame(calculateAverageFrequency);
    }

    calculateAverageFrequency();

    function animate(averageFrequency) {
      camera.position.x += (mouseX - camera.position.x) * 0.05;
      camera.position.y += (-mouseY - camera.position.y) * 0.5;
      camera.lookAt(scene.position);
      uniforms.u_time.value = clock.getElapsedTime();
      uniforms.u_frequency.value = averageFrequency;
      bloomComposer.render();
    }

    window.addEventListener("resize", function () {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      bloomComposer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  componentWillUnmount() {
    // Code to stop using the mic when the component is removed
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => track.stop());
    }
  }

  render() {
    return (
      <Container maxWidth={10000} sx={{ padding: 0, margin: 0 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Emotion Detection
        </Typography>
        <div className={styles.Container}>
          <div className={styles.ThreeContainer}>
            <div
              ref={this.threeDivRef}
              style={{
                ...getDims(),
                borderRadius: "1rem",
              }}
            />
          </div>

          <div className={styles.DetailsContainer}>
            {this.state.aaiLoaded ? (
              <>
                <Typography variant="h6">Detected Emotions</Typography>
                <div className={styles.TagContainer}>
                  {this.state.humeDetails.length === 0 ? (
                    <Alert severity="info">
                      Please speak to detect emotions in speech.
                    </Alert>
                  ) : (
                    this.state.humeDetails.map((emotion, index) => (
                      <div key={index} className={styles.Tag}>
                        <div className={styles.TagInternal}>
                          <div
                            style={{
                              background: emotionColors[emotion.name],
                              borderRadius: "50%",
                              width: 10,
                              height: 10,
                            }}
                          ></div>
                          <div className={styles.EmotionName}>
                            {emotion.name}
                          </div>
                          <div
                            style={{
                              marginLeft: "auto",
                            }}
                          >
                            <Typography variant="caption">
                              {emotion.score.toFixed(2)}
                            </Typography>
                          </div>
                        </div>
                        <Box sx={{ width: "100%" }}>
                          <LinearProgress
                            variant="determinate"
                            value={emotion.score * 100}
                          />
                        </Box>
                      </div>
                    ))
                  )}
                </div>

                <Divider sx={{ margin: "1rem 0" }} />
                <div>
                  <Typography variant="h6">Transcription</Typography>
                  <div id="transcription" className={styles.Transcription}>
                    {this.state.transcript.length > 0 ? (
                      this.state.transcript
                    ) : (
                      <Alert severity="info">
                        Please speak to start transcribing.
                      </Alert>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className={styles.LoaderContainer}>
                <Typography variant="caption">Loading...</Typography>
                <CircularProgress label="loading" />
              </div>
            )}
          </div>
        </div>
      </Container>
    );
  }
}

export default WithAPICall(Eq);
